// BigTournamentShortBoardDisplay.js
import React, { useCallback, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './Board.css';
import './BigTournamentShortResponsive.css';
import { useGame } from './context/GameContext';
import { useSound } from './sfx/SoundProvider';
import tournamentSocket from './bigTournamentShortSocket';

const BIG_SHORT_BOARD_WIDTH = 1020;
const BIG_SHORT_BOARD_FALLBACK_HEIGHT = 640;

const TournamentBoardDisplay = ({
  tournamentBoard,
  tournamentBoardState,
  countdownMessage,
  tournamentPlayer1Name,
  tournamentPlayer2Name,
  tournamentOpponentName,
  handleTournamentFirstMove,
  tournamentFirstRolls,
  tournamentPlayer1Roll,
  tournamentPlayer2Roll,
  tournamentCountdown,
  tournamentHandleReadyClick,
  tournamentSchetchik,
  tournamentFirstRollComplete,
  tournamentShowReadyButton,
  tournamentHasRolledDice,
  tournamentCurrentTurn,
  handleTournamentRollDiceClick,
  tournamentShowUndoButton,
  tournamentHandleUndoMove,
  tournamentNumberMove,
  tournamentConfirmMove,
  tournamentWhiteOffBoard,
  tournamentSetWhiteOffBoard,
  tournamentBlackOffBoard,
  tournamentSetBlackOffBoard,
  tournamentOnPointClick,
  handleTournamentDragOver, // (оставляем для совместимости, но ниже используем tournamentHandleDragOver)
  tournamentHandleDragStart,
  handleTournamentDrop,     // (оставляем для совместимости, но ниже используем tournamentHandleDrop)
  tournamentOnCheckerClick,
  tournamentHandleDrop,
  tournamentHandleDragOver,
  tournamentHandleFirstRollClick,
  firstMoveComplete,
  player1Time,
  player2Time,
  tournamentTimeoutMsg,
  modalMessage,
  showModal,
  tournamentShowSurrenderButton,
  tournamentHandleSurrender,
  player1Avatar,
  player2Avatar,
  player1ShortRating,
  player2ShortRating,
  tournamentStageLabel,
  tournamentTypeLabel,
  tournamentPrize,
  autoRoll,
  autoConfirm,
  onToggleAutoRoll,
  onToggleAutoConfirm,

  // остальные пропсы
}) => {
  const { t: translate } = useTranslation();

  /* ====== контекст игры ====== */
  const {
    tournamentCapturedCheckers,
    tournamentRolledDice,
    tournamentReadyStatus,
    tournamentRollEnabled,
    setTournamentRollEnabled,
    tournamentGame,
    setTournamentFirstRollComplete,
    tournamentIsFirstMoveDone,
    tournamentFlagFirst,
    setTournamentFlagFirst,
    setTournamentRolledDice,
    setTournamentBoardState,
    tournamentPlayerColor,
    setTournamentPlayerColor,
    tournamentOpponentColor,
    setTournamentOpponentColor,
   
  } = useGame();

  /* ====== SFX ====== */
  const { play } = useSound();
  const playSfx = (name) => {
    try {
      const p = play?.(name);
      if (p && typeof p.then === 'function') p.catch(() => {});
    } catch (_) {}
  };

  /* =========================================================
     Изолированный responsive только большого короткого турнира.
     На ПК исходная разметка остаётся прежней. На телефоне и
     планшете активируются только классы с префиксом bts-.
     ========================================================= */
  const shellRef = useRef(null);
  const boardCanvasRef = useRef(null);
  const boardImageRef = useRef(null);
  const resizeFrameRef = useRef(null);

  const [responsiveLayout, setResponsiveLayout] = useState({
    scale: 1,
    boardWidth: BIG_SHORT_BOARD_WIDTH,
    boardHeight: BIG_SHORT_BOARD_FALLBACK_HEIGHT,
    slotWidth: BIG_SHORT_BOARD_WIDTH,
    slotHeight: BIG_SHORT_BOARD_FALLBACK_HEIGHT,
    viewportWidth: BIG_SHORT_BOARD_WIDTH,
    viewportHeight: 700,
    isLandscape: false,
    isCompact: false,
    fillsViewport: false,
  });

  const recalculateResponsiveLayout = useCallback(() => {
    if (typeof window === 'undefined') return;

    if (resizeFrameRef.current !== null) {
      window.cancelAnimationFrame(resizeFrameRef.current);
    }

    resizeFrameRef.current = window.requestAnimationFrame(() => {
      resizeFrameRef.current = null;

      const visualViewport = window.visualViewport;
      const documentElement = document.documentElement;

      /*
       * DevTools Responsive может менять layout viewport быстрее, чем
       * visualViewport. Поэтому для адаптива берём текущий clientWidth/clientHeight
       * страницы, а visualViewport оставляем только запасным источником.
       */
      const viewportWidth = Math.max(
        280,
        Math.round(
          documentElement?.clientWidth ||
          window.innerWidth ||
          visualViewport?.width ||
          BIG_SHORT_BOARD_WIDTH
        )
      );
      const viewportHeight = Math.max(
        240,
        Math.round(
          documentElement?.clientHeight ||
          window.innerHeight ||
          visualViewport?.height ||
          700
        )
      );

      const imageElement = boardImageRef.current;
      const boardContentWidth = BIG_SHORT_BOARD_WIDTH - 40;
      const measuredHeight =
        imageElement?.naturalWidth > 0 && imageElement?.naturalHeight > 0
          ? Math.round(
              boardContentWidth *
              (imageElement.naturalHeight / imageElement.naturalWidth)
            ) + 40
          : BIG_SHORT_BOARD_FALLBACK_HEIGHT;

      const isLandscape = viewportWidth > viewportHeight;

      /*
       * Старый desktop HUD начинает ломаться примерно ниже 1450px.
       * С этого порога переходим в устойчивый compact HUD над доской.
       */
      const isCompact = viewportWidth <= 1450 || viewportHeight <= 760;
      const fillsViewport = Boolean(
        isLandscape &&
        viewportWidth <= 1100 &&
        viewportHeight <= 600
      );

      let scale = 1;
      let slotWidth = BIG_SHORT_BOARD_WIDTH;
      let slotHeight = measuredHeight;

      if (fillsViewport) {
        slotWidth = viewportWidth;
        slotHeight = viewportHeight;
      } else if (isCompact) {
        const availableWidth = Math.max(260, viewportWidth - 16);
        const availableHeight = isLandscape
          ? Math.max(240, viewportHeight - 112)
          : measuredHeight;

        scale = Math.max(
          0.22,
          Math.min(
            1,
            availableWidth / BIG_SHORT_BOARD_WIDTH,
            availableHeight / measuredHeight
          )
        );

        slotWidth = Math.max(
          1,
          Math.round(BIG_SHORT_BOARD_WIDTH * scale)
        );
        slotHeight = Math.max(
          1,
          Math.round(measuredHeight * scale)
        );
      }

      const roundedScale = Math.round(scale * 10000) / 10000;

      setResponsiveLayout(previous => {
        const unchanged =
          Math.abs(previous.scale - roundedScale) < 0.0001 &&
          previous.boardHeight === measuredHeight &&
          previous.slotWidth === slotWidth &&
          previous.slotHeight === slotHeight &&
          previous.viewportWidth === viewportWidth &&
          previous.viewportHeight === viewportHeight &&
          previous.isLandscape === isLandscape &&
          previous.isCompact === isCompact &&
          previous.fillsViewport === fillsViewport;

        if (unchanged) return previous;

        return {
          scale: roundedScale,
          boardWidth: BIG_SHORT_BOARD_WIDTH,
          boardHeight: measuredHeight,
          slotWidth,
          slotHeight,
          viewportWidth,
          viewportHeight,
          isLandscape,
          isCompact,
          fillsViewport,
        };
      });
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const schedule = () => recalculateResponsiveLayout();
    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(schedule)
        : null;

    if (boardImageRef.current && resizeObserver) {
      resizeObserver.observe(boardImageRef.current);
    }
    if (document.documentElement && resizeObserver) {
      resizeObserver.observe(document.documentElement);
    }

    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('orientationchange', schedule, { passive: true });
    window.visualViewport?.addEventListener('resize', schedule, {
      passive: true,
    });

    schedule();
    const delayedMeasure = window.setTimeout(schedule, 180);

    return () => {
      window.clearTimeout(delayedMeasure);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', schedule);
      window.visualViewport?.removeEventListener('resize', schedule);

      if (resizeFrameRef.current !== null) {
        window.cancelAnimationFrame(resizeFrameRef.current);
        resizeFrameRef.current = null;
      }
    };
  }, [recalculateResponsiveLayout]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!responsiveLayout.fillsViewport) return undefined;

    const html = document.documentElement;
    const body = document.body;
    const previousHtmlOverflow = html.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    const previousBodyMargin = body.style.margin;

    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.margin = '0';
    html.classList.add('bts-mobile-short-open');
    body.classList.add('bts-mobile-short-open');

    return () => {
      html.style.overflow = previousHtmlOverflow;
      body.style.overflow = previousBodyOverflow;
      body.style.margin = previousBodyMargin;
      html.classList.remove('bts-mobile-short-open');
      body.classList.remove('bts-mobile-short-open');
    };
  }, [responsiveLayout.fillsViewport]);


  // обёртки с SFX
  const onPointClickSfx = (pt, label) => {
    if (Date.now() <= suppressClickUntilRef.current) return;
    playSfx('click');
    tournamentOnPointClick?.(pt, label);
  };
  const onDragStartSfx = (e, from, idx, fromBar = false) => {
    playSfx('click');
    tournamentHandleDragStart?.(e, from, idx, fromBar);
  };
  const onDropSfx = (e, to) => {
    playSfx('drop');
    (tournamentHandleDrop || handleTournamentDrop)?.(e, to);
  };
  const onUndoSfx = () => {
    playSfx('cancel');
    tournamentHandleUndoMove?.();
  };
  const onConfirmSfx = () => {
    playSfx('ok');
    tournamentConfirmMove?.();
  };
  // const onRollSfx = () => {
  //   playSfx('dice');
  //   handleTournamentRollDiceClick?.();
  // };
    const onRollSfx = () => {
    // ЗВУК БОЛЬШЕ НЕ ИГРАЕМ ЗДЕСЬ
    // Звук будет запускаться при фактическом появлении кубиков
    handleTournamentRollDiceClick?.();
  };

  const onSurrenderSfx = () => {
    playSfx('pass');
    tournamentHandleSurrender?.();
  };







  // ⬇️ ДОБАВЬ: локальный статус сети/сокета
const [net, setNet] = useState({
  online: tournamentSocket.connected,
  trying: !tournamentSocket.connected,
  attempt: 0,
  reason: null,
});


// ⬇️ ДОБАВЬ: подписки на события сокета + реакция на online/offline браузера
// useEffect(() => {
//   const onConnect = () =>
//     setNet({ online: true, trying: false, attempt: 0, reason: null });

//   const onDisconnect = (reason) => {
//     setNet((s) => ({ ...s, online: false, trying: true, reason: reason || 'disconnect' }));
//     // Если сервер сам разорвал соединение — инициируем переподключение
//     if (reason === 'io server disconnect') {
//       setTimeout(() => tournamentSocket.connect(), 500);
//     }
//   };

//   const onConnectError = (err) =>
//     setNet((s) => ({ ...s, online: false, trying: true, reason: err?.message || 'connect_error' }));

//   // Попытки реконнекта (через менеджер)
//   const onAttempt = (n) =>
//     setNet((s) => ({ ...s, trying: true, attempt: n || s.attempt + 1 }));

//   const onReconnected = () =>
//     setNet({ online: true, trying: false, attempt: 0, reason: null });

//   // Подписки
//   tournamentSocket.on('connect', onConnect);
//   tournamentSocket.on('disconnect', onDisconnect);
//   tournamentSocket.on('connect_error', onConnectError);

//   // события менеджера (кол-во попыток и пр.)
//   tournamentSocket.io?.on?.('reconnect_attempt', onAttempt);
//   tournamentSocket.io?.on?.('reconnect', onReconnected);
//   tournamentSocket.io?.on?.('reconnect_error', onConnectError);
//   tournamentSocket.io?.on?.('reconnect_failed', () =>
//     setNet((s) => ({ ...s, trying: false, reason: 'reconnect_failed' }))
//   );

//   // online/offline браузера — подсказываем пользователю и пробуем коннект
//   const goOffline = () =>
//     setNet((s) => ({ ...s, online: false, trying: true, reason: 'offline' }));
//   const goOnline = () => {
//     setNet((s) => ({ ...s, trying: true }));
//     tournamentSocket.connect();
//   };
//   window.addEventListener('offline', goOffline);
//   window.addEventListener('online', goOnline);

//   return () => {
//     tournamentSocket.off('connect', onConnect);
//     tournamentSocket.off('disconnect', onDisconnect);
//     tournamentSocket.off('connect_error', onConnectError);
//     tournamentSocket.io?.off?.('reconnect_attempt', onAttempt);
//     tournamentSocket.io?.off?.('reconnect', onReconnected);
//     tournamentSocket.io?.off?.('reconnect_error', onConnectError);
//     tournamentSocket.io?.off?.('reconnect_failed');
//     window.removeEventListener('offline', goOffline);
//     window.removeEventListener('online', goOnline);
//   };
// }, []);


  // «goodluck» один раз после определения, кто ходит первым (с мобильным фолбэком)
  const goodluckPlayedRef     = useRef(false);
  const gestureHandlerRef     = useRef(null);
  const suppressClickUntilRef = useRef(0);


    const hadDiceRef = useRef(false);
  // Предыдущее состояние первых бросков (кто ходит первым)
  const prevFirstRollsRef = useRef({
    player1Roll: null,
    player2Roll: null,
  });

  /* ====== отладочные логи (как у тебя) ====== */
  useEffect(() => {
    console.log('56756567575675', tournamentRolledDice, tournamentFirstRolls);
  }, [tournamentRolledDice, tournamentFirstRolls]);

  useEffect(() => {
    console.log('[TournamentBoardDisplay] Полученное состояние доски:', tournamentBoardState, tournamentBoard);
  }, [tournamentBoard, tournamentBoardState]);

  useEffect(() => {
    console.log('[tournamentNumberMove] Получено состояние:', tournamentHasRolledDice, tournamentCurrentTurn, tournamentPlayerColor);
  }, [tournamentHasRolledDice, tournamentCurrentTurn, tournamentPlayerColor]);



    // 🔊 Звук кубиков при обычных бросках (ручных и авто, своих и чужих)
  useEffect(() => {
    const hasDice =
      Array.isArray(tournamentRolledDice) && tournamentRolledDice.length > 0;

    // Переход "не было кубиков" -> "появились кубики"
    if (hasDice && !hadDiceRef.current) {
      hadDiceRef.current = true;
      playSfx('dice'); // этот звук услышат оба игрока
    }
    // Переход "кубики были" -> "кубиков нет" (конец хода)
    else if (!hasDice && hadDiceRef.current) {
      hadDiceRef.current = false;
    }
  }, [tournamentRolledDice, play]);

  // 🔊 Звук кубиков при первых бросках (определение, кто ходит первым)
  useEffect(() => {
    if (!tournamentFirstRolls) return;

    const prev = prevFirstRollsRef.current || {};
    const curr = tournamentFirstRolls;

    const p1Changed =
      Number.isInteger(curr.player1Roll) &&
      curr.player1Roll !== prev.player1Roll;

    const p2Changed =
      Number.isInteger(curr.player2Roll) &&
      curr.player2Roll !== prev.player2Roll;

    // Если у кого-то из игроков появился новый первый бросок — играем звук
    if (p1Changed || p2Changed) {
      playSfx('dice');
    }

    prevFirstRollsRef.current = {
      player1Roll: curr.player1Roll,
      player2Roll: curr.player2Roll,
    };
  }, [tournamentFirstRolls, play]);

  /* ====== мой/оппонент цвет ====== */
  const myTournamentColor =
    tournamentPlayerColor === 'white' || tournamentPlayerColor === 'black'
      ? tournamentPlayerColor
      : 'white';
  const opponentTournamentColor = myTournamentColor === 'white' ? 'black' : 'white';

  useEffect(() => {
    if (tournamentPlayerColor) {
      const opponentColor = tournamentPlayerColor === 'white' ? 'black' : 'white';
      setTournamentOpponentColor?.(opponentColor);
      console.log('[Tournament] Opponent Color:', opponentColor);
    }
  }, [tournamentPlayerColor, setTournamentOpponentColor]);

  useEffect(() => {
    console.log('Render Roll Dice Button Condition:', tournamentHasRolledDice, tournamentCurrentTurn, tournamentNumberMove, tournamentRolledDice);
  }, [tournamentHasRolledDice, tournamentCurrentTurn, tournamentNumberMove, tournamentRolledDice]);

  const playerReady =
    myTournamentColor === 'white'
      ? (tournamentReadyStatus?.player1 || false)
      : (tournamentReadyStatus?.player2 || false);

  useEffect(() => {
    console.log('[TournamentBoardDisplay] Смотрим на первые броски:', tournamentFirstRolls);
    console.log('Player1Roll:', tournamentPlayer1Roll, 'Player2Roll:', tournamentPlayer2Roll);
  }, [tournamentFirstRolls, tournamentPlayer1Roll, tournamentPlayer2Roll]);

  useEffect(() => {
    console.log('TournamentBoardDisplay props:', {
      tournamentBoard,
      tournamentOpponentName,
      tournamentPlayerColor,
      tournamentOpponentColor,
    });
  }, [tournamentBoard, tournamentOpponentName, tournamentPlayerColor, tournamentOpponentColor]);




useEffect(() => {
  // Этот компонент только следит за состоянием соединения.
  // Вход за стол и загрузку игры выполняет BigTournamentShortMatch.js.
  const onConnect = () => {
    setNet({
      online: true,
      trying: false,
      attempt: 0,
      reason: null,
    });
  };

  const onDisconnect = (reason) => {
    setNet((current) => ({
      ...current,
      online: false,
      trying: true,
      reason: reason || 'disconnect',
    }));
  };

  const onConnectError = (error) => {
    setNet((current) => ({
      ...current,
      online: false,
      trying: true,
      reason: error?.message || 'connect_error',
    }));
  };

  const onReconnectAttempt = (attempt) => {
    setNet((current) => ({
      ...current,
      trying: true,
      attempt: Number(attempt) || current.attempt + 1,
    }));
  };

  const onReconnect = () => {
    setNet({
      online: true,
      trying: false,
      attempt: 0,
      reason: null,
    });
  };

  const onReconnectFailed = () => {
    setNet((current) => ({
      ...current,
      online: false,
      trying: false,
      reason: 'reconnect_failed',
    }));
  };

  const goOffline = () => {
    setNet((current) => ({
      ...current,
      online: false,
      trying: true,
      reason: 'offline',
    }));
  };

  const goOnline = () => {
    setNet((current) => ({
      ...current,
      trying: true,
      reason: null,
    }));
  };

  tournamentSocket.on('connect', onConnect);
  tournamentSocket.on('disconnect', onDisconnect);
  tournamentSocket.on('connect_error', onConnectError);

  tournamentSocket.io?.on?.(
    'reconnect_attempt',
    onReconnectAttempt
  );
  tournamentSocket.io?.on?.(
    'reconnect',
    onReconnect
  );
  tournamentSocket.io?.on?.(
    'reconnect_error',
    onConnectError
  );
  tournamentSocket.io?.on?.(
    'reconnect_failed',
    onReconnectFailed
  );

  window.addEventListener('offline', goOffline);
  window.addEventListener('online', goOnline);

  if (tournamentSocket.connected) {
    onConnect();
  }

  return () => {
    tournamentSocket.off('connect', onConnect);
    tournamentSocket.off('disconnect', onDisconnect);
    tournamentSocket.off('connect_error', onConnectError);

    tournamentSocket.io?.off?.(
      'reconnect_attempt',
      onReconnectAttempt
    );
    tournamentSocket.io?.off?.(
      'reconnect',
      onReconnect
    );
    tournamentSocket.io?.off?.(
      'reconnect_error',
      onConnectError
    );
    tournamentSocket.io?.off?.(
      'reconnect_failed',
      onReconnectFailed
    );

    window.removeEventListener('offline', goOffline);
    window.removeEventListener('online', goOnline);
  };
}, []);


// рядом с остальными SFX-обёртками
const onDragOverSfx = (e) => {
  // ВАЖНО: всегда разрешаем drop
  if (e && typeof e.preventDefault === 'function') e.preventDefault();
  (tournamentHandleDragOver || handleTournamentDragOver)?.(e);
};


  /* =========================================================
     TOUCH DND ДЛЯ ТЕЛЕФОНА

     Используется та же игровая логика, что и при drag/drop мышью.
     Здесь решаются только три мобильные задачи:
     1) видимый фантом шашки;
     2) стабильные touch-события между React-render;
     3) широкая зона попадания по колонке пункта.
     ========================================================= */
  const touchDrag = useRef({
    dragging: false,
    fromPoint: null,
    fromIndex: null,
    color: null,
    fromBar: false,
    ghost: null,
    sourceElement: null,
    targetElement: null,
    targetDropIndex: null,
    startX: 0,
    startY: 0,
  });

  // Родительский tournamentHandleDragStart записывает сюда dragData
  // через виртуальный dataTransfer. Затем те же данные получает drop.
  const touchDataRef = useRef({});
  const touchMovedRef = useRef(false);

  const canDragChecker = (checkerColor) => {
    const myColorNow =
      tournamentPlayerColor === 'white' || tournamentPlayerColor === 'black'
        ? tournamentPlayerColor
        : null;

    const myTurn =
      !!myColorNow &&
      tournamentCurrentTurn ===
        (myColorNow === 'white' ? 'player1' : 'player2');

    if (!myTurn || !myColorNow) return false;

    if (
      checkerColor &&
      String(checkerColor).toLowerCase() !== String(myColorNow).toLowerCase()
    ) {
      return false;
    }

    // Остальные правила проверяет существующий tournamentHandleDrop.
    return true;
  };

  const clearTouchMoveHighlight = () => {
    const boardElement = boardCanvasRef.current;
    if (!boardElement) return;

    boardElement
      .querySelectorAll(
        '.checker--origin, .point--origin, .point--target, .checker.hl, .point-container.hl, .point-containerd.hl'
      )
      .forEach((element) => {
        element.classList.remove(
          'checker--origin',
          'point--origin',
          'point--target',
          'hl'
        );
      });
  };

  const clearTouchDropTarget = () => {
    const current = touchDrag.current;
    current.targetElement = null;
    current.targetDropIndex = null;
  };

  const cleanupTouchDrag = () => {
    try {
      const current = touchDrag.current;

      current.sourceElement?.classList.remove('bts-drag-source');
      current.ghost?.remove();
    } catch (_) {}

    touchDrag.current = {
      dragging: false,
      fromPoint: null,
      fromIndex: null,
      color: null,
      fromBar: false,
      ghost: null,
      sourceElement: null,
      targetElement: null,
      targetDropIndex: null,
      startX: 0,
      startY: 0,
    };
  };

  const createTouchDataTransfer = () => ({
    setData(type, value) {
      const key = type || 'text';
      const stringValue = String(value ?? '');
      touchDataRef.current[key] = stringValue;

      // Некоторые обработчики используют text, другие text/plain.
      if (key === 'text') touchDataRef.current['text/plain'] = stringValue;
      if (key === 'text/plain') touchDataRef.current.text = stringValue;
    },
    getData(type) {
      const key = type || 'text';
      return (
        touchDataRef.current[key] ??
        (key === 'text/plain' ? touchDataRef.current.text : undefined) ??
        (key === 'text' ? touchDataRef.current['text/plain'] : undefined) ??
        ''
      );
    },
    clearData(type) {
      if (!type) {
        touchDataRef.current = {};
      } else {
        delete touchDataRef.current[type];
      }
    },
    effectAllowed: 'move',
    dropEffect: 'move',
  });

  const positionTouchGhost = (ghost, clientX, clientY) => {
    if (!ghost) return;

    // ВАЖНО: задаём координаты с priority=important.
    // В старой версии CSS-свойство inset с !important блокировало left/top,
    // поэтому фантом создавался, но визуально не шёл за пальцем.
    ghost.style.setProperty('left', `${clientX}px`, 'important');
    ghost.style.setProperty('top', `${clientY - 28}px`, 'important');
  };

  const createTouchGhost = (sourceElement, touch, color) => {
    const sourceRect = sourceElement.getBoundingClientRect();
    const computed = window.getComputedStyle(sourceElement);
    const size = Math.max(sourceRect.width || 0, sourceRect.height || 0, 38);
    const normalizedColor = String(color || '').toLowerCase();

    // Создаём отдельную шашку, как в рабочей кеш-игре.
    // Не клонируем DOM исходной шашки: клон мог наследовать transform/position
    // стека и становиться невидимым вне игрового пункта.
    const ghost = document.createElement('div');
    ghost.className = `checker ${normalizedColor} drag-ghost bts-touch-ghost`;

    const stackCount = sourceElement.querySelector?.('.stack-count')?.textContent;
    if (stackCount) {
      const count = document.createElement('span');
      count.className = 'stack-count';
      count.textContent = stackCount;
      ghost.appendChild(count);
    }

    const fallbackBackground = normalizedColor === 'black'
      ? 'radial-gradient(circle at 34% 28%, #494949 0%, #202020 48%, #070707 100%)'
      : 'radial-gradient(circle at 34% 28%, #fffdf8 0%, #eee2d1 52%, #cdbca6 100%)';

    Object.assign(ghost.style, {
      position: 'fixed',
      width: `${size}px`,
      minWidth: `${size}px`,
      maxWidth: `${size}px`,
      height: `${size}px`,
      minHeight: `${size}px`,
      maxHeight: `${size}px`,
      margin: '0',
      opacity: '0.82',
      visibility: 'visible',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: computed.background && computed.background !== 'rgba(0, 0, 0, 0)'
        ? computed.background
        : fallbackBackground,
      backgroundColor: computed.backgroundColor,
      backgroundImage:
        computed.backgroundImage && computed.backgroundImage !== 'none'
          ? computed.backgroundImage
          : fallbackBackground,
      backgroundSize: computed.backgroundSize,
      backgroundPosition: computed.backgroundPosition,
      border: computed.border && computed.border !== '0px none rgb(0, 0, 0)'
        ? computed.border
        : normalizedColor === 'black'
          ? '2px solid rgba(225, 244, 246, 0.78)'
          : '2px solid rgba(116, 91, 67, 0.52)',
      borderRadius: '50%',
      color: computed.color,
      zIndex: '2147483647',
      pointerEvents: 'none',
    });

    document.body.appendChild(ghost);
    positionTouchGhost(ghost, touch.clientX, touch.clientY);
    return ghost;
  };

  // Ищем пункт не только под точным местом отпускания.
  // Вся вертикальная колонка верхней/нижней половины считается зоной пункта.
  const resolveTouchDropTarget = (clientX, clientY) => {
    const boardElement = boardCanvasRef.current;
    if (!boardElement) return null;

    const boardRect = boardElement.getBoundingClientRect();
    const outsidePadding = Math.max(22, boardRect.width * 0.025);

    if (
      clientX < boardRect.left - outsidePadding ||
      clientX > boardRect.right + outsidePadding ||
      clientY < boardRect.top - outsidePadding ||
      clientY > boardRect.bottom + outsidePadding
    ) {
      return null;
    }

    const topHalf = clientY < boardRect.top + boardRect.height / 2;
    const selector = topHalf
      ? '.top-row [data-bts-drop-index]'
      : '.bottom-row [data-bts-drop-index]';

    const candidates = Array.from(boardElement.querySelectorAll(selector));
    if (candidates.length === 0) return null;

    let best = null;

    candidates.forEach((element) => {
      const rect = element.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const horizontalOutside =
        clientX < rect.left
          ? rect.left - clientX
          : clientX > rect.right
            ? clientX - rect.right
            : 0;

      // Сначала предпочитаем элемент, в ширину которого попал палец.
      // Если палец между двумя пунктами — выбираем ближайший центр.
      const score = horizontalOutside * 8 + Math.abs(clientX - centerX);

      if (!best || score < best.score) {
        best = { element, rect, score, horizontalOutside };
      }
    });

    if (!best) return null;

    const maximumSnapDistance = Math.max(44, best.rect.width * 1.35);
    if (best.horizontalOutside > maximumSnapDistance) return null;

    const dropIndex = Number(best.element.dataset.btsDropIndex);
    if (!Number.isInteger(dropIndex) || dropIndex < 0 || dropIndex > 23) {
      return null;
    }

    return {
      element: best.element,
      dropIndex,
    };
  };

  const updateTouchDropTarget = (clientX, clientY) => {
    const current = touchDrag.current;
    const target = resolveTouchDropTarget(clientX, clientY);

    current.targetElement = target?.element || null;
    current.targetDropIndex = Number.isInteger(target?.dropIndex)
      ? target.dropIndex
      : null;

    return target;
  };

  const handleTouchStart = (
    event,
    pointIndex,
    checkerIndex,
    color,
    fromBar = false
  ) => {
    cleanupTouchDrag();
    touchMovedRef.current = false;

    if (!canDragChecker(color)) return;
    if (!event.touches || event.touches.length === 0) return;

    // На touchstart ничего не блокируем и не создаём фантом.
    // Это важно: короткий тап должен сохранить прежний ход по клику.
    // Реальное перетаскивание запускаем только после движения пальца.
    const touch = event.touches[0];
    const sourceElement = event.currentTarget;

    touchDrag.current.dragging = true;
    touchDrag.current.fromPoint = pointIndex;
    touchDrag.current.fromIndex = checkerIndex;
    touchDrag.current.color = color;
    touchDrag.current.fromBar = fromBar;
    touchDrag.current.sourceElement = sourceElement;
    touchDrag.current.startX = touch.clientX;
    touchDrag.current.startY = touch.clientY;

    touchDataRef.current = {};
  };

  const activateTouchDrag = (event, touch) => {
    const current = touchDrag.current;
    if (!current.dragging || touchMovedRef.current) return;

    touchMovedRef.current = true;
    current.sourceElement?.classList.add('bts-drag-source');
    clearTouchMoveHighlight();

    current.ghost = createTouchGhost(
      current.sourceElement,
      touch,
      current.color
    );

    const dragEvent = {
      type: 'dragstart',
      target: current.sourceElement,
      currentTarget: current.sourceElement,
      preventDefault() {},
      stopPropagation() {},
      dataTransfer: createTouchDataTransfer(),
    };

    onDragStartSfx(
      dragEvent,
      current.fromPoint,
      current.fromIndex,
      current.fromBar
    );

    // Страховка: родительский обработчик обычно записывает эти данные сам.
    if (!touchDataRef.current.text && !touchDataRef.current['text/plain']) {
      const payload = JSON.stringify({
        pointIndex: current.fromPoint,
        checkerIndex: current.fromIndex,
        isFromBar: current.fromBar,
      });
      touchDataRef.current.text = payload;
      touchDataRef.current['text/plain'] = payload;
    }

    suppressClickUntilRef.current = Date.now() + 1200;
  };

  const handleTouchMove = (event) => {
    const current = touchDrag.current;
    if (!current.dragging) return;
    if (!event.touches || event.touches.length === 0) return;

    const touch = event.touches[0];
    const distance = Math.hypot(
      touch.clientX - current.startX,
      touch.clientY - current.startY
    );

    // Небольшое движение пальца считаем обычным тапом.
    // После 7 px включаем настоящий drag-and-drop и показываем фантом.
    if (!touchMovedRef.current) {
      if (distance < 7) return;
      activateTouchDrag(event, touch);
    }

    if (!touchMovedRef.current || !current.ghost) return;

    positionTouchGhost(current.ghost, touch.clientX, touch.clientY);
    updateTouchDropTarget(touch.clientX, touch.clientY);

    suppressClickUntilRef.current = Date.now() + 1200;

    if (event.cancelable) event.preventDefault();
    event.stopPropagation?.();
  };

  const handleTouchEnd = (event) => {
    const current = touchDrag.current;
    const wasDragging = !!current.dragging;
    const wasMoved = touchMovedRef.current;

    // Сохраняем источник ДО cleanupTouchDrag(), потому что cleanup сбрасывает ref.
    const sourcePoint = current.fromPoint;
    const sourceColor = current.color;
    const sourceWasBar = current.fromBar;

    let target = null;
    if (wasMoved && event.changedTouches && event.changedTouches.length > 0) {
      const { clientX, clientY } = event.changedTouches[0];
      target =
        resolveTouchDropTarget(clientX, clientY) ||
        (current.targetElement && Number.isInteger(current.targetDropIndex)
          ? {
              element: current.targetElement,
              dropIndex: current.targetDropIndex,
            }
          : null);
    }

    cleanupTouchDrag();
    touchMovedRef.current = false;

    if (!wasDragging) {
      touchDataRef.current = {};
      return;
    }

    // Короткий тап: возвращаем прежнюю отдельную логику хода по клику.
    // Синтетический browser click подавляем, чтобы ход не выполнился дважды.
    if (!wasMoved) {
      suppressClickUntilRef.current = Date.now() + 650;
      touchDataRef.current = {};

      if (sourceWasBar) {
        playSfx('click');
        tournamentOnCheckerClick?.(25, sourceColor);
      } else if (Number.isInteger(sourcePoint)) {
        playSfx('click');
        tournamentOnPointClick?.(sourcePoint, sourcePoint + 1);
      }

      return;
    }

    if (!target) {
      touchDataRef.current = {};
      return;
    }

    const dropEvent = {
      type: 'drop',
      target: target.element,
      currentTarget: target.element,
      preventDefault() {},
      stopPropagation() {},
      dataTransfer: createTouchDataTransfer(),
    };

    suppressClickUntilRef.current = Date.now() + 1200;
    onDropSfx(dropEvent, target.dropIndex);
    touchDataRef.current = {};
  };

  const handleTouchCancel = () => {
    touchMovedRef.current = false;
    cleanupTouchDrag();
    touchDataRef.current = {};
  };

  // Важный момент: listeners создаются один раз. Раньше эффект без []
  // пересоздавался при каждом тике таймера и мог удалить фантом/dragData
  // прямо между touchstart и touchend.
  const latestTouchHandlersRef = useRef({
    move: null,
    end: null,
    cancel: null,
  });

  latestTouchHandlersRef.current.move = handleTouchMove;
  latestTouchHandlersRef.current.end = handleTouchEnd;
  latestTouchHandlersRef.current.cancel = handleTouchCancel;

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const onWindowTouchMove = (event) =>
      latestTouchHandlersRef.current.move?.(event);
    const onWindowTouchEnd = (event) =>
      latestTouchHandlersRef.current.end?.(event);
    const onWindowTouchCancel = (event) =>
      latestTouchHandlersRef.current.cancel?.(event);

    window.addEventListener('touchmove', onWindowTouchMove, { passive: false });
    window.addEventListener('touchend', onWindowTouchEnd, { passive: false });
    window.addEventListener('touchcancel', onWindowTouchCancel, {
      passive: false,
    });

    return () => {
      window.removeEventListener('touchmove', onWindowTouchMove);
      window.removeEventListener('touchend', onWindowTouchEnd);
      window.removeEventListener('touchcancel', onWindowTouchCancel);
      cleanupTouchDrag();
      touchDataRef.current = {};
    };
  }, []);


  /* ====== картинки кубиков ====== */
  const diceImages = {
    white: {
      1: '/img/k1-white-1.png',
      2: '/img/k1-white-2.png',
      3: '/img/k1-white-3.png',
      4: '/img/k1-white-4.png',
      5: '/img/k1-white-5.png',
      6: '/img/k1-white-6.png',
    },
    black: {
      1: '/img/k1-black-1.png',
      2: '/img/k1-black-2.png',
      3: '/img/k1-black-3.png',
      4: '/img/k1-black-4.png',
      5: '/img/k1-black-5.png',
      6: '/img/k1-black-6.png',
    },
  };

  console.log('myTournamentColor=', myTournamentColor);
  console.log('tournamentCapturedCheckers=', tournamentCapturedCheckers);

  /* ====== валидность доски (без раннего return) ====== */
  const boardReady = Array.isArray(tournamentBoard) && tournamentBoard.length === 24;
  if (!boardReady && process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.warn('[TournamentBoardDisplay] board ещё не готов:', tournamentBoard);
  }

  /* ====== AVATAR URL RESOLVER ====== */
  const RAW_API =
    (typeof import.meta !== 'undefined' && (import.meta.env?.VITE_API_ORIGIN || import.meta.env?.VITE_API_URL)) ||
    process.env.REACT_APP_API_ORIGIN ||
    process.env.REACT_APP_API_URL ||
    '';

  const PAGE_PROTOCOL = (typeof window !== 'undefined' && window.location?.protocol) || 'http:';
  const PAGE_HOST     = (typeof window !== 'undefined' && window.location?.hostname) || 'localhost';
  const ENV_PORT =
    (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_PORT) ||
    process.env.REACT_APP_PORT ||
    (typeof window !== 'undefined' && window.location?.port === '3001' ? '5000' : '');

  let API_ORIGIN = '';
  if (/^https?:\/\//i.test(RAW_API)) {
    API_ORIGIN = RAW_API.replace(/\/+$/, '');
  } else if (RAW_API) {
    const hasPort = /:\d+$/.test(RAW_API);
    API_ORIGIN = `${PAGE_PROTOCOL}//${RAW_API}${hasPort ? '' : (ENV_PORT ? `:${ENV_PORT}` : '')}`.replace(/\/+$/, '');
  } else {
    API_ORIGIN = `${PAGE_PROTOCOL}//${PAGE_HOST}${ENV_PORT ? `:${ENV_PORT}` : ''}`.replace(/\/+$/, '');
  }

  const DEFAULT_AVATAR =
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96">
         <circle cx="48" cy="48" r="46" fill="#2b2b2b" stroke="#999" stroke-width="2"/>
       </svg>`
    );

  function resolveAvatarSrc(raw) {
    const s = String(raw || '').replace(/\\/g, '/').trim();
    if (!s) return DEFAULT_AVATAR;
    if (/^https?:\/\//i.test(s)) return s;
    if (/^\/?uploads\//i.test(s) || /^\/?api\/uploads\//i.test(s)) {
      return `${API_ORIGIN}/${s.replace(/^\/+/, '')}`;
    }
    return s.startsWith('/') ? s : `/${s}`;
  }

  const p1Src = resolveAvatarSrc(player1Avatar);
  const p2Src = resolveAvatarSrc(player2Avatar);

  // Пробуем прелоадить аватарки (хук всегда вызывается)
  useEffect(() => {
    const test = (label, url) => {
      if (!url || url === DEFAULT_AVATAR) return;
      const img = new Image();
      img.onload = () => console.log(`[AVATAR][probe] ${label}: OK →`, url);
      img.onerror = (e) => console.warn(`[AVATAR][probe] ${label}: ERROR →`, url, e);
      img.src = url;
    };
    test('P1', p1Src);
    test('P2', p2Src);
  }, [p1Src, p2Src]);

  // Мини-аватар с фолбэком на инициал, если src пустой/сломался
  const TbdAvatar = ({ src, name }) => {
    const [ok, setOk] = useState(Boolean(src));
    const initial = (name || '').trim().charAt(0).toUpperCase() || '•';

    if (!ok) {
      return <div className="tbd-avatar tbd-avatar-fallback">{initial}</div>;
    }
    return (
      <img
        className="tbd-avatar"
        src={src}
        alt={name || 'player'}
        onError={() => setOk(false)}
      />
    );
  };

  /* ====== формат времени ====== */
  function formatTime(seconds) {
    const min = Math.floor(seconds / 60).toString().padStart(1, '0');
    const sec = (seconds % 60).toString().padStart(2, '0');
    return `${min}:${sec}`;
  }

  /* ====== разбиение доски ====== */
  const firstBlack = Array.isArray(tournamentBoard) && tournamentBoard.length >= 24
    ? tournamentBoard.slice(0, 12)
    : [];
  const secondBlack = Array.isArray(tournamentBoard) && tournamentBoard.length >= 24
    ? tournamentBoard.slice(12).reverse()
    : [];
  const firstWhite = Array.isArray(tournamentBoard) && tournamentBoard.length >= 24
    ? tournamentBoard.slice(12).reverse()
    : [];
  const secondWhite = Array.isArray(tournamentBoard) && tournamentBoard.length >= 24
    ? tournamentBoard.slice(0, 12)
    : [];

  /* ====== goodluck SFX после первых бросков ====== */
  useEffect(() => {
    const ready =
      Number.isInteger(tournamentFirstRolls?.player1Roll) &&
      Number.isInteger(tournamentFirstRolls?.player2Roll);

    if (!ready) return;
    if (goodluckPlayedRef.current) return;

    if (gestureHandlerRef.current) {
      window.removeEventListener('pointerdown', gestureHandlerRef.current);
      gestureHandlerRef.current = null;
    }

    const onFirstGesture = async () => {
      if (goodluckPlayedRef.current) return;
      try {
        const r = play?.('goodluck');
        if (r && typeof r.then === 'function') await r;
      } catch (_) {
        // ignore
      } finally {
        goodluckPlayedRef.current = true;
        suppressClickUntilRef.current = Date.now() + 250;
        window.removeEventListener('pointerdown', onFirstGesture);
        gestureHandlerRef.current = null;
      }
    };

    try {
      const maybe = play?.('goodluck');
      if (maybe && typeof maybe.then === 'function') {
        maybe.then(() => {
          goodluckPlayedRef.current = true;
          suppressClickUntilRef.current = Date.now() + 250;
        }).catch(() => {
          if (!goodluckPlayedRef.current) {
            gestureHandlerRef.current = onFirstGesture;
            window.addEventListener('pointerdown', onFirstGesture, { once: true, passive: true });
          }
        });
      } else {
        gestureHandlerRef.current = onFirstGesture;
        window.addEventListener('pointerdown', onFirstGesture, { once: true, passive: true });
      }
    } catch (_) {
      gestureHandlerRef.current = onFirstGesture;
      window.addEventListener('pointerdown', onFirstGesture, { once: true, passive: true });
    }

    return () => {
      if (gestureHandlerRef.current) {
        window.removeEventListener('pointerdown', gestureHandlerRef.current);
        gestureHandlerRef.current = null;
      }
    };
  }, [tournamentFirstRolls, play]);

  /* ====== компактная отрисовка стека шашек (макс 5 + число) ====== */
  function renderStackedCheckers(point, { isTopRow, realIndex }) {
    const checkers = Array.isArray(point) ? point : [];
    const visible  = checkers.slice(0, 5);
    const total    = checkers.length;

    return visible.map((checker, i) => {
      if (!checker || !checker.color) return null;
      const color = String(checker.color).toLowerCase();
      const showCount = total > 5 ? (isTopRow ? i === visible.length - 1 : i === 0) : false;

      return (
        <div
          key={i}
          className={`checker ${color}`}
          draggable={!(responsiveLayout.isCompact || isPortraitPhone)}
          onDragStart={(e) => onDragStartSfx(e, realIndex, i)}
          onTouchStart={(e) =>
            handleTouchStart(e, realIndex, i, color, false)
          }
          style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {showCount && <span className="stack-count">{total}</span>}
        </div>
      );
    });
  }

  /* ====== точки (для белого/чёрного) ====== */
  const renderTournamentPoints = () => {
    if (myTournamentColor === 'white') {
      // == Для белого ==
      return (
        <>
          {/* Верхний ряд (white) */}
          <div className="top-row">
            {(firstWhite || []).map((point, index) => (
              <div
                key={index}
                className={`point-container p${index}`}
                data-bts-drop-index={24 - index - 1}
                onClick={() => onPointClickSfx(24 - index - 1, 24 - index)}
                onDragOver={onDragOverSfx}
                onDrop={(e) => onDropSfx(e, 24 - index - 1)}
              >
                <span>{24 - index}</span>
                {renderStackedCheckers(point, { isTopRow: true, realIndex: 24 - index - 1 })}
              </div>
            ))}
          </div>

          {/* Нижний ряд (white) */}
          <div className="bottom-row">
            {(secondWhite || []).map((point, index) => (
              <div
                key={index}
                className={`point-containerd r${index}`}
                data-bts-drop-index={index}
                onClick={() => onPointClickSfx(index, index + 1)}
                onDragOver={onDragOverSfx}

                onDrop={(e) => onDropSfx(e, index)}
              >
                {renderStackedCheckers(point, { isTopRow: false, realIndex: index })}
                <span>{index + 1}</span>
              </div>
            ))}
          </div>
        </>
      );
    }

    // == Для чёрного ==
    return (
      <>
        {/* Верхний ряд (black) */}
        <div className="top-row">
          {(firstBlack || []).map((point, index) => (
            <div
              key={index}
              className={`point-container p${index}`}
              data-bts-drop-index={index}
              onClick={() => onPointClickSfx(index, index + 1)}
              onDragOver={onDragOverSfx}
              onDrop={(e) => onDropSfx(e, index)}
            >
              <span>{index + 1}</span>
              {renderStackedCheckers(point, { isTopRow: true, realIndex: index })}
            </div>
          ))}
        </div>

        {/* Нижний ряд (black) */}
        <div className="bottom-row">
          {(secondBlack || []).map((point, index) => (
            <div
              key={index}
              className={`point-containerd r${index}`}
              data-bts-drop-index={24 - index - 1}
              onClick={() => onPointClickSfx(24 - index - 1, 24 - index)}
              onDragOver={onDragOverSfx}
              onDrop={(e) => onDropSfx(e, 24 - index - 1)}
            >
              {renderStackedCheckers(point, { isTopRow: false, realIndex: 24 - index - 1 })}
              <span style={{ color: 'white' }}>{24 - index}</span>
            </div>
          ))}
        </div>
      </>
    );
  };

  const getTournamentLabelClass = (rawValue) => {
    const value = String(rawValue || '').toLowerCase();

    if (value.includes('полу') || value.includes('semi')) return 'semifinal';
    if (value.includes('финал') || value.includes('final')) return 'final';
    if (value.includes('квалиф') || value.includes('qualif')) {
      return 'qualification';
    }
    if (
      value.includes('реба') ||
      value.includes('rebuy') ||
      value.includes('re-buy')
    ) {
      return 'rebuy';
    }

    return '';
  };

  const localizeTournamentLabel = (rawValue) => {
    const raw = String(rawValue || '').trim();
    const value = raw.toLowerCase();

    if (!raw) return '';
    if (value.includes('полу') || value.includes('semi')) {
      return translate('jackpotBoard.stage.semifinal');
    }
    if (value.includes('финал') || value.includes('final')) {
      return translate('jackpotBoard.stage.final');
    }
    if (value.includes('квалиф') || value.includes('qualif')) {
      return translate('jackpotBoard.stage.qualification');
    }
    if (
      value.includes('реба') ||
      value.includes('rebuy') ||
      value.includes('re-buy')
    ) {
      return translate('jackpotBoard.stage.rebuy');
    }

    return raw;
  };

  const displayStageLabel = localizeTournamentLabel(tournamentStageLabel);
  const displayTournamentTypeLabel = localizeTournamentLabel(
    tournamentTypeLabel
  );
  const responsiveTournamentLabels = [
    displayTournamentTypeLabel,
    displayStageLabel,
  ].filter((label, index, labels) => label && labels.indexOf(label) === index);

  const renderResponsivePlayerCard = ({
    side,
    src,
    name,
    rating,
    time,
    active,
  }) => (
    <div
      className={`bts-player-card bts-player-card--${side} ${
        active ? 'is-active' : ''
      }`}
    >
      <TbdAvatar src={src} name={name} />
      <div className="bts-player-card__meta">
        <div className="bts-player-card__name" title={name || ''}>
          {name || ''}
        </div>
        <div className="bts-player-card__rating">
          ELO {rating ?? '—'}
        </div>
        <div className="bts-player-card__time">
          {formatTime(time)}
        </div>
      </div>
    </div>
  );

  const viewportNowWidth = responsiveLayout.viewportWidth;
  const viewportNowHeight = responsiveLayout.viewportHeight;

  const isPortraitPhone =
    viewportNowWidth <= 600 &&
    viewportNowHeight > viewportNowWidth &&
    !responsiveLayout.fillsViewport;

  const responsiveStyle = {
    '--bts-board-width': `${responsiveLayout.boardWidth}px`,
    '--bts-board-height': `${responsiveLayout.boardHeight}px`,
    '--bts-board-scale': responsiveLayout.scale,
    '--bts-slot-width': `${responsiveLayout.slotWidth}px`,
    '--bts-slot-height': `${responsiveLayout.slotHeight}px`,
  };

  const shellClassName = [
    'bts-shell',
    (responsiveLayout.isCompact || isPortraitPhone) ? 'bts-shell--compact' : '',
    responsiveLayout.isLandscape ? 'bts-shell--landscape' : '',
    isPortraitPhone ? 'bts-shell--portrait-phone' : '',
    responsiveLayout.fillsViewport ? 'bts-shell--fullscreen' : '',
  ].filter(Boolean).join(' ');

  /* ====== JSX ====== */
  const shellContent = (
    <div
      ref={shellRef}
      className={shellClassName}
      style={responsiveStyle}
    >
      {(responsiveLayout.isCompact || isPortraitPhone) &&
        !responsiveLayout.fillsViewport && (
          <header className="bts-compact-hud">
            {renderResponsivePlayerCard({
              side: 'left',
              src: p1Src,
              name: tournamentPlayer1Name,
              rating: player1ShortRating,
              time: player1Time,
              active: tournamentCurrentTurn === 'player1',
            })}

            <div className="bts-compact-hud__center">
              {responsiveTournamentLabels.length > 0 && (
                <div className="bts-stage-labels">
                  {responsiveTournamentLabels.map((label, index) => (
                    <span
                      key={`${label}-${index}`}
                      className={`bts-stage-label ${
                        getTournamentLabelClass(label)
                      }`}
                    >
                      {label}
                    </span>
                  ))}
                </div>
              )}

              {tournamentShowSurrenderButton && (
                <button
                  type="button"
                  className="bts-surrender-button"
                  onClick={onSurrenderSfx}
                  aria-label={translate('jackpotBoard.resign')}
                  title={translate('jackpotBoard.resign')}
                >
                  <span aria-hidden="true">🏳️</span>
                </button>
              )}
            </div>

            {renderResponsivePlayerCard({
              side: 'right',
              src: p2Src,
              name: tournamentPlayer2Name,
              rating: player2ShortRating,
              time: player2Time,
              active: tournamentCurrentTurn === 'player2',
            })}
          </header>
        )}

      <main className="bts-board-viewport">
        <div className="bts-board-slot">
          <div className="bts-board-scale-layer">
            <div
              ref={boardCanvasRef}
              className={`board tbd-pos theme-neo big-short-tournament-board bts-board-canvas ${
                /(финал|final)/i.test(tournamentStageLabel || '') &&
                !/(полуфин|semi)/i.test(tournamentStageLabel || '')
                  ? 'is-final'
                  : 'is-semi'
              } ${myTournamentColor === 'black' ? 'flipped' : ''}`}
            >
  {/* ⬇️ ДОБАВЬ: лёгкая шторка во время реконнекта/обрыва */}
{(!net.online || net.trying) && <div className="tbd-netshade" />}

      {/* Баннер по таймауту */}
      {tournamentTimeoutMsg && (
        <div className={'timeout-banner ' + (tournamentTimeoutMsg.victory ? 'victory' : 'defeat')}>
          {tournamentTimeoutMsg.text}
        </div>
      )}

      {/* Если доска ещё не готова — мягкий лоадер, БЕЗ раннего return */}
      {!boardReady && <div className="loading">{translate('jackpotBoard.loading')}</div>}

      <img
        ref={boardImageRef}
        className="bts-board-image"
        src="/img/Board33.png"
        alt="Board"
        draggable="false"
        onLoad={recalculateResponsiveLayout}
      />

      {/* Аватарки и рейтинг по краям доски */}
      <div className="tbd-players-overlay">
        {/* Заголовок над доской */}
         {!(responsiveLayout.isCompact || isPortraitPhone) && (
           <div className="tbd-stagebar">
             {tournamentStageLabel ? <div className="tbd-stage">{displayStageLabel}</div> : null}
             {tournamentPrize ? <div className="tbd-prize">{translate('jackpotBoard.prize')}: {tournamentPrize}</div> : null}
           </div>
         )}





{/* ⬇️ ДОБАВЬ: чип статуса сети */}
{(net.trying || !net.online) && (
  <div
    className={`tbd-conn-chip ${!net.online ? 'is-down' : 'is-trying'}`}
    role="status"
    aria-live="polite"
  >
    {navigator.onLine === false
      ? translate('jackpotBoard.noInternet')
      : !net.online
      ? translate('jackpotBoard.connectionLost')
      : translate('jackpotBoard.reconnecting', { attempt: net.attempt || 1 })}
    {net.reason && <span className="tbd-conn-reason"> ({net.reason})</span>}
  </div>
)}



        {/* Кнопка «Сдаться» как оверлей */}
        {!(responsiveLayout.isCompact || isPortraitPhone) && tournamentShowSurrenderButton && (
          <button className="tbd-surrender" onClick={onSurrenderSfx} type="button">
            {translate('jackpotBoard.resign')}
          </button>
        )}

     {!(responsiveLayout.isCompact || isPortraitPhone) && (
       <div className="tbd-side tbd-left tbd-card">
         <TbdAvatar src={p1Src} name={tournamentPlayer1Name} />
         <div className="tbd-meta">
           <div className="tbd-name">{tournamentPlayer1Name || ''}</div>
           {player1ShortRating && <div className="tbd-rating">{player1ShortRating}</div>}
           <div className="tbd-time">{formatTime(player1Time)}</div>
         </div>
       </div>
     )}



   {!(responsiveLayout.isCompact || isPortraitPhone) && (
     <div className="tbd-side tbd-right tbd-card">
       <TbdAvatar src={p2Src} name={tournamentPlayer2Name} />
       <div className="tbd-meta">
         <div className="tbd-name">{tournamentPlayer2Name || ''}</div>
         {player2ShortRating && <div className="tbd-rating">{player2ShortRating}</div>}
         <div className="tbd-time">{formatTime(player2Time)}</div>
       </div>
     </div>
   )}


      </div>

      {responsiveLayout.fillsViewport && (
        <div className="bts-mobile-rail-hud">
          {renderResponsivePlayerCard({
            side: 'rail-top',
            src: p1Src,
            name: tournamentPlayer1Name,
            rating: player1ShortRating,
            time: player1Time,
            active: tournamentCurrentTurn === 'player1',
          })}

          <div className="bts-mobile-rail-center">
            {responsiveTournamentLabels.length > 0 && (
              <div className="bts-mobile-stage">
                {responsiveTournamentLabels.map((label, index) => (
                  <span
                    key={`${label}-${index}`}
                    className={`bts-mobile-stage__line ${
                      getTournamentLabelClass(label)
                    }`}
                  >
                    {label}
                  </span>
                ))}
              </div>
            )}

            {tournamentShowSurrenderButton && (
              <button
                type="button"
                className="bts-mobile-surrender"
                onClick={onSurrenderSfx}
                aria-label={translate('jackpotBoard.resign')}
                title={translate('jackpotBoard.resign')}
              >
                <span aria-hidden="true">🏳️</span>
              </button>
            )}
          </div>

          {renderResponsivePlayerCard({
            side: 'rail-bottom',
            src: p2Src,
            name: tournamentPlayer2Name,
            rating: player2ShortRating,
            time: player2Time,
            active: tournamentCurrentTurn === 'player2',
          })}
        </div>
      )}

      {/* Первый бросок (до первого хода) */}
      {!firstMoveComplete && tournamentFirstRolls && (
        <div className="first-rolls-display">
          {Number.isInteger(tournamentFirstRolls?.player1Roll) && (
            <img
              src={diceImages.white[tournamentFirstRolls.player1Roll]}
              alt={translate('jackpotBoard.firstRollP1', { value: tournamentFirstRolls.player1Roll })}
              className="dice-image first-roll-player1"
            />
          )}
          {Number.isInteger(tournamentFirstRolls?.player2Roll) && (
            <img
              src={diceImages.black[tournamentFirstRolls.player2Roll]}
              alt={translate('jackpotBoard.firstRollP2', { value: tournamentFirstRolls.player2Roll })}
              className="dice-image first-roll-player2"
            />
          )}
        </div>
      )}

      {/* Кнопка "Бросить кубики" после первого броска (если мой ход) */}
      {/* {!tournamentHasRolledDice &&
        tournamentFirstRollComplete &&
        tournamentCurrentTurn === (tournamentPlayerColor === 'white' ? 'player1' : 'player2') && (
          <button className="roll-dice-button" onClick={onRollSfx}>
            {translate('jackpotBoard.rollDice')}
          </button>
        )} */}
{!tournamentHasRolledDice &&
  tournamentFirstRollComplete &&
  tournamentCurrentTurn === (tournamentPlayerColor === 'white' ? 'player1' : 'player2') &&
  net.online && !net.trying && (
    <button className="roll-dice-button" onClick={onRollSfx} disabled={!net.online || net.trying}>
      {translate('jackpotBoard.rollDice')}
    </button>
)}

      {/* Отрисовка выпавших кубиков во время обычного хода */}
      {tournamentRolledDice && tournamentRolledDice.length > 0 && firstMoveComplete && (
        <div className="dice-display">
          {tournamentRolledDice.map((val, idx) => {
            const isCurrentPlayer =
              tournamentCurrentTurn === (tournamentPlayerColor === 'white' ? 'player1' : 'player2');
            const diceSrc = isCurrentPlayer
              ? diceImages[tournamentPlayerColor]?.[val]
              : diceImages[opponentTournamentColor]?.[val];
            return <img key={idx} src={diceSrc} alt={`Dice ${val}`} />;
          })}
        </div>
      )}

      {/* Кнопка "Отмена" хода */}
      {/* {tournamentShowUndoButton && (
        <button className="undo-button" onClick={onUndoSfx}>
          {translate('jackpotBoard.undo')}
        </button>
      )} */}
{tournamentShowUndoButton && net.online && !net.trying && (
  <button className="undo-button" onClick={onUndoSfx} disabled={!net.online || net.trying}>
    {translate('jackpotBoard.undo')}
  </button>
)}

      {/* Кнопка "Завершить ход" */}
    {/* {tournamentHasRolledDice && */}
  {/* {tournamentCurrentTurn === (tournamentPlayerColor === 'white' ? 'player1' : 'player2') &&
  tournamentNumberMove === 0 && ( */}
  
 {/* {tournamentCurrentTurn === (tournamentPlayerColor === 'white' ? 'player1' : 'player2') &&
 tournamentNumberMove === 0 && (

    <button className="ok-button" onClick={onConfirmSfx}>
      <img src="/img/Ok.png" alt={translate('jackpotBoard.finishMove')} />
    </button>
)} */}
{tournamentHasRolledDice &&
  Array.isArray(tournamentRolledDice) &&
  tournamentRolledDice.length >= 2 &&
  tournamentCurrentTurn === (tournamentPlayerColor === 'white' ? 'player1' : 'player2') &&
  Number(tournamentNumberMove) <= 0 &&
  net.online &&
  !net.trying && (
    <button
      className="ok-button"
      onClick={onConfirmSfx}
      disabled={!net.online || net.trying}
    >
      <img src="/img/Ok.png" alt={translate('jackpotBoard.finishMove')} />
    </button>
)}


      {/* Рисуем ячейки (top/bottom) */}
      {renderTournamentPoints()}

      {/* BAR: убитые шашки */}
      <div className="bar">
        {/* Шашки оппонента (верх бара) */}
        <div className="bar-opponentColor">
          {tournamentCapturedCheckers?.[opponentTournamentColor] > 0 && (
            <div
              className={`checker ${opponentTournamentColor} ${
                opponentTournamentColor === 'black' ? 'checkerBar' : 'checkerWhite'
              }`}
              draggable={!(responsiveLayout.isCompact || isPortraitPhone)}
              onDragStart={(e) => onDragStartSfx(e, 25, 0, true)}
              onTouchStart={(e) =>
                handleTouchStart(
                  e,
                  25,
                  0,
                  opponentTournamentColor,
                  true
                )
              }
            >
              {tournamentCapturedCheckers[opponentTournamentColor] > 1
                ? tournamentCapturedCheckers[opponentTournamentColor]
                : opponentTournamentColor}
            </div>
          )}
        </div>

        {/* Ваши шашки (низ бара) */}
        <div className="bar-myColor">
          {tournamentCapturedCheckers?.[myTournamentColor] > 0 && (
            <div
              className={`checker ${myTournamentColor} ${
                myTournamentColor === 'black' ? 'checkerBar' : 'checkerWhite'
              }`}
              draggable={!(responsiveLayout.isCompact || isPortraitPhone)}
              onDragStart={(e) => onDragStartSfx(e, 25, 0, true)}
              onTouchStart={(e) =>
                handleTouchStart(e, 25, 0, myTournamentColor, true)
              }
              onClick={() => {
                if (Date.now() <= suppressClickUntilRef.current) return;
                tournamentOnCheckerClick?.(25, myTournamentColor);
              }}
            >
              {tournamentCapturedCheckers[myTournamentColor] > 1
                ? tournamentCapturedCheckers[myTournamentColor]
                : myTournamentColor}
            </div>
          )}
        </div>
      </div>

      {/* Off-board (выведенные шашки) */}
      <div className="off-board bts-off-board">
        {/*
          Оппонент — верхний лоток.
          Позиции рассчитаны на максимальные 15 шашек: даже полный стек
          остаётся внутри своей мобильной ячейки и показывает каждую шашку.
        */}
        <div className="off-board-opponent bts-off-tray bts-off-tray--opponent">
          {(opponentTournamentColor === 'white'
            ? (tournamentWhiteOffBoard || [])
            : (tournamentBlackOffBoard || [])
          ).map((_, idx) => {
            const slotIndex = Math.min(idx, 14);
            const positionPercent = 10 + (slotIndex * 80) / 14;

            return (
              <div
                key={idx}
                className={`checker ${opponentTournamentColor} bts-off-checker bts-off-checker--opponent`}
                style={{
                  '--bts-off-position': `${positionPercent}%`,
                  '--bts-off-z': idx + 1,
                }}
                aria-hidden="true"
              />
            );
          })}
        </div>

        {/*
          Мои выведенные шашки — нижний лоток. Стек растёт снизу вверх,
          как в настольных нардах, и также вмещает все 15 шашек.
        */}
        <div className="off-board-my bts-off-tray bts-off-tray--my">
          {(myTournamentColor === 'white'
            ? (tournamentWhiteOffBoard || [])
            : (tournamentBlackOffBoard || [])
          ).map((_, idx) => {
            const slotIndex = Math.min(idx, 14);
            const positionPercent = 90 - (slotIndex * 80) / 14;

            return (
              <div
                key={idx}
                className={`checker ${myTournamentColor} bts-off-checker bts-off-checker--my`}
                style={{
                  '--bts-off-position': `${positionPercent}%`,
                  '--bts-off-z': idx + 1,
                }}
                aria-hidden="true"
              />
            );
          })}
        </div>
      </div>

            </div>
          </div>
        </div>
      </main>

      <div
        className="bts-auto-actions"
        role="group"
        aria-label={translate('jackpotBoard.autoSettingsShort')}
      >
        {firstMoveComplete && (
          <button
            type="button"
            className={`bts-auto-button ${autoRoll ? 'on' : ''}`}
            onClick={onToggleAutoRoll}
          >
            {translate('jackpotBoard.autoRoll')}: {' '}
            {autoRoll
              ? translate('jackpotBoard.on')
              : translate('jackpotBoard.off')}
          </button>
        )}

        {firstMoveComplete && (
          <button
            type="button"
            className={`bts-auto-button ${autoConfirm ? 'on' : ''}`}
            onClick={onToggleAutoConfirm}
          >
            {translate('jackpotBoard.autoConfirmShort')}: {' '}
            {autoConfirm
              ? translate('jackpotBoard.on')
              : translate('jackpotBoard.off')}
          </button>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay bts-local-modal-overlay">
          <div className="modal-content bts-local-modal-content">
            <p>{modalMessage}</p>
          </div>
        </div>
      )}
    </div>
  );

  if (
    isPortraitPhone &&
    typeof document !== 'undefined' &&
    document.body
  ) {
    return createPortal(shellContent, document.body);
  }

  return shellContent;
};

export default TournamentBoardDisplay;